export type FontFaceConfig = {
  url: string;
  weight: number;
};

export type FontConfig = {
  name: string;
  family: string;
  faces: FontFaceConfig[];
};

export type PresentationThemeConfig = {
  text: {
    colors: { primary: string; secondary: string };
    font: FontConfig;
  };
  title: {
    colors: { primary: string; secondary: string };
    font: FontConfig;
  };
  colors?: {
    background?: string;
    background2?: string;
    border2?: string;
  };
};

export type PresentationTheme = {
  id: string;
  label: string;
  theme: PresentationThemeConfig;
};

export const DEFAULT_PRESENTATION_THEME = {
  id: "default",
  label: "Default",
};

/**
 * A mapping of subscription plan IDs to their available branded themes.
 */
export const presentationThemesByPlan: Record<string, PresentationTheme[]> = {
  "ttr-sir": [
    {
      id: "sothebys",
      label: "SIR - Light",
      theme: {
        text: {
          colors: {
            primary: "#002349",
            secondary: "#666666",
          },
          font: {
            name: "Benton Sans",
            family: '"Benton Sans", Arial, sans-serif',
            faces: [
              { url: "/fonts/sothebys/BentonSans-Book_sOT.woff2", weight: 400 },
              {
                url: "/fonts/sothebys/BentonSans-Medium_sOT_1.woff2",
                weight: 500,
              },
            ],
          },
        },
        title: {
          colors: { primary: "#002349", secondary: "#666666" },
          font: {
            name: "FreightDisp Pro",
            family: '"FreightDisp Pro", Garamond, serif',
            faces: [
              { url: "/fonts/sothebys/FreigDisProLig.woff2", weight: 300 },
              {
                url: "/fonts/sothebys/GarageFonts - FreightDisp Pro Book.woff2",
                weight: 400,
              },
            ],
          },
        },
        colors: {
          background: "#FFFFFF",
          background2: "#EDECE6",
        },
      },
    },
    {
      id: "sothebys-dark",
      label: "SIR - Dark",
      theme: {
        text: {
          colors: {
            primary: "#FFFFFF",
            secondary: "#EDECE6",
          },
          font: {
            name: "Benton Sans",
            family: '"Benton Sans", Arial, sans-serif',
            faces: [
              { url: "/fonts/sothebys/BentonSans-Book_sOT.woff2", weight: 400 },
              {
                url: "/fonts/sothebys/BentonSans-Medium_sOT_1.woff2",
                weight: 500,
              },
            ],
          },
        },
        title: {
          colors: { primary: "#EDECE6", secondary: "#CCCCCC" },
          font: {
            name: "FreightDisp Pro",
            family: '"FreightDisp Pro", Garamond, serif',
            faces: [
              { url: "/fonts/sothebys/FreigDisProLig.woff2", weight: 300 },
              {
                url: "/fonts/sothebys/GarageFonts - FreightDisp Pro Book.woff2",
                weight: 400,
              },
            ],
          },
        },
        colors: {
          background: "#002349",
          background2: "#001830",
          border2: "#1A3A6A",
        },
      },
    },
  ],
} as const;

export const getThemesForPlan = (
  planId: string | null | undefined,
): PresentationTheme[] =>
  planId ? (presentationThemesByPlan[planId] ?? []) : [];

export const findThemeById = (id: string): PresentationTheme | undefined =>
  Object.values(presentationThemesByPlan)
    .flat()
    .find((t) => t.id === id);

/**
 * CSS font shorthand specs (e.g. `400 1em "Benton Sans"`) for every face of a
 * theme, suitable for `document.fonts.load()`. Used to force-load themed
 * fonts before PDF capture, since they aren't guaranteed to already be
 * queued by `document.fonts.ready` at that point.
 */
export const getThemeFontLoadSpecs = (theme: PresentationTheme): string[] =>
  [theme.theme.text.font, theme.theme.title.font].flatMap(({ name, faces }) =>
    faces.map(({ weight }) => `${weight} 1em "${name}"`),
  );
